<template>
    <ds-modal
        class="quick-add-modal"
        data-qa="quick-add-modal"
        size="fullscreen"
        is-open
        show-close
        :title="titleText"
        @close="redirectForClose"
    >
        <div
            v-if="errorLoadingInitialState"
            class="network-error"
            data-qa="network-error-initial-load"
        >
            <DsInlineAlert leading-icon type="critical">
                <template #default>
                    {{ $t('myaccount.quick.add.network.error.sentence') }}
                </template>

                <template #footer>
                    <DsFilledButton gray dense @click="load">
                        {{ $t('myaccount.quick.add.error.try.again.button') }}
                    </DsFilledButton>
                    <DsTextButton gray dense @click="goToSupportChat">
                        {{ $t('myaccount.quick.add.error.get.help') }}
                    </DsTextButton>
                </template>
            </DsInlineAlert>
        </div>

        <div v-else>
            <template v-if="showLoading">
                <ds-placeholder
                    class="placeholder"
                    :rows="placeholder"
                />
            </template>

            <quick-add-wizard
                v-else
                :type-of-addon="localTypeOfAddon"
                :selected-wizard-stage="selectedWizardStage"
                :selected-tier="selectedTier"
                :current-tier="currentTier"
                :subscription-details="subscriptionDetails"
                :contact-details="contactDetails"
                :confirmation-loading-status="confirmationLoadingStatus"
                @close-scheduled-change-notice="closeScheduledChangeNotice"
                @change-estimate-loaded="setChangeEstimate"
                @set-selected-wizard-stage="setCurrentStage"
                @set-confirmation-loading-status="setConfirmationLoadingStatus"
                @error-loading-change-estimate="setErrorLoadingChangeEstimate"
            >
                <template v-slot:default>
                    <div v-if="isSelectionStage">
                        <div
                            v-if="errorLoadingChangeEstimate"
                            class="network-error"
                            data-qa="network-error-selection-stage"
                        >
                            <DsInlineAlert leading-icon type="critical">
                                <template #default>
                                    <p class="network-error-paragraph">
                                        {{ $t('myaccount.quick.add.change.summary.network.error.sentence') }}

                                        <a
                                            class="contact-support-link"
                                            data-qa="contact-support-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            :href="contactSupportLink"
                                        >
                                            {{ $t('myaccount.quick.add.change.summary.confirm.error.contact.support') }}
                                        </a>
                                    </p>
                                </template>
                            </DsInlineAlert>
                        </div>

                        <quick-add-quantity-adjuster
                            data-qa="quantity-adjuster"
                            :quick-add-type="localTypeOfAddon"
                            :current-tier="currentTier"
                            :subscription-details="subscriptionDetails"
                            :contact-details="contactDetails"
                            :parent-selected-tier="selectedTier"
                            @selected-tier-change="changeSelectedTier"
                        />
                    </div>
                    <div v-else-if="confirmationLoadingStatusIsNotIdle && isConfirmationLoadingStatusNotError">
                        <confirmation-result
                            new-total-quantity="0"
                            :loading-status="confirmationLoadingStatus"
                            :subscription-details="subscriptionDetails"
                            :contact-details="contactDetails"
                            :selected-tier="selectedTier"
                            :current-tier="currentTier"
                        />
                    </div>
                    <div v-else-if="isVerificationStage">
                        <quick-add-change-summary
                            :type-of-addon="localTypeOfAddon"
                            :contact-details="contactDetails"
                            :current-tier="currentTier"
                            :selected-tier="selectedTier"
                            :charge-amount="proratedEstimateTotal"
                            :change-estimate="changeEstimate"
                            :subscription-details="subscriptionDetails"
                            :selected-wizard-stage="selectedWizardStage"
                            :confirmation-loading-status="confirmationLoadingStatus"
                            @set-selected-wizard-stage="setCurrentStage"
                        />
                    </div>
                </template>
            </quick-add-wizard>
        </div>
    </ds-modal>
</template>

<script>
import amplitude from '@/components/util/analytics/amplitude';
import axios from 'axios';
import ConfirmationResult from './ConfirmationResult.vue';
import QuickAddWizard from './QuickAddWizard.vue';
import QuickAddQuantityAdjuster from './QuickAddQuantityAdjuster.vue';
import QuickAddChangeSummary from './QuickAddChangeSummary.vue';

const SELECTION_STAGE = 0;
const VERIFY_STAGE = 1;
const CONTACTS_QUICK_ADD = 'CONTACTS_QUICK_ADD';
const INITIAL_LOAD = 'INITIAL_LOAD';
const IDLE = 'idle';
const ERROR = 'error';
const CONTACT_SUPPORT_LINK = 'https://help.infusionsoft.com/help/chat-status';
const AMPLITUDE_ERROR_STATUS = 'ERROR';
const AMPLITUDE_SUCCESS_STATUS = 'SUCCESS';
const AMPLITUDE_INITIAL_LOAD_STEP = 'Initial Load';

export default {
    components: {
        ConfirmationResult,
        QuickAddChangeSummary,
        QuickAddQuantityAdjuster,
        QuickAddWizard,
    },

    props: {
        parentTypeOfAddon: String,
        redirectAfterCloseUrl: String,
    },

    emits: ['close'],

    data() {
        return {
            selectedWizardStage: SELECTION_STAGE,
            contactPricingTiers: [],
            contactUsed: 0,
            contactLimit: 0,
            estimate: {},
            currencySymbol: '',
            subscriptionStatus: '',
            scheduledToCancel: false,
            differences: null,
            nextBillingAt: '',
            periodUnit: '',
            scheduledChangesEstimateTotal: 0,
            hasScheduledChanges: false,
            showScheduledChangePrePage: false,
            lineItems: [],
            showLoading: false,
            selectedTier: -1,
            currentTier: -1,
            placeholder: [
                { height: '2rem', boxes: [1] },
                { height: '2rem', boxes: [1] },
                { height: '2rem', boxes: [1] },
            ],
            changeEstimate: {},
            confirmationLoadingStatus: IDLE,
            errorLoadingInitialState: false,
            localTypeOfAddon: '',
            errorLoadingChangeEstimate: false,
            contactSupportLink: CONTACT_SUPPORT_LINK,
        };
    },

    created() {
        this.formatAddonType();

        this.load();
    },

    computed: {
        titleText() {
            let addonType = this.localTypeOfAddon.toLowerCase();

            // handle pluralization
            if (addonType === 'contact') {
                addonType = 'contacts';
            }

            return this.$t('myaccount.quick.add.title', { type: addonType });
        },

        doShowScheduledChangeNotice() {
            return this.showScheduledChangePrePage && this.hasScheduledChanges;
        },

        isSelectionStage() {
            return this.selectedWizardStage === SELECTION_STAGE;
        },

        isVerificationStage() {
            return this.selectedWizardStage === VERIFY_STAGE && !this.doShowScheduledChangeNotice;
        },

        subscriptionDetails() {
            return {
                differences: this.differences,
                nextBillingAt: this.nextBillingAt,
                billingFrequency: this.periodUnit,
                currencySymbol: this.currencySymbol,
                subscriptionStatus: this.subscriptionStatus,
                scheduledChangesEstimateTotal: this.scheduledChangesEstimateTotal,
                showScheduledChangesNotice: this.doShowScheduledChangeNotice,
                hasScheduledChanges: this.hasScheduledChanges,
                lineItems: this.lineItems,
                estimate: this.estimate,
                scheduledToCancel: this.scheduledToCancel,
            };
        },

        contactDetails() {
            return {
                contactUsed: this.contactUsed,
                contactLimit: this.contactLimit,
                contactPricingTiers: this.contactPricingTiers,
            };
        },

        confirmationLoadingStatusIsNotIdle() {
            return this.confirmationLoadingStatus !== IDLE;
        },

        isConfirmationLoadingStatusNotError() {
            return this.confirmationLoadingStatus !== ERROR;
        },

        supportUrl() {
            return 'https://help.infusionsoft.com/help/chat-status';
        },

        proratedEstimateTotal() {
            return this.changeEstimate?.proratedEstimate?.total;
        },
    },

    methods: {
        async load() {
            this.errorLoadingInitialState = false;
            this.showLoading = true;

            try {
                const { data: { additionalContactsTiers } } = await axios.get(
                    '/app/myAccount/prices/additional-contacts',
                );

                if (additionalContactsTiers === null && Object.keys(additionalContactsTiers).length === 0) {
                    throw new Error('Empty prices returned from KSM');
                }

                this.contactPricingTiers = additionalContactsTiers.priceTiers;

                const contactLimitResponse = await axios.get('/app/contact/contactLimit');

                this.contactLimit = contactLimitResponse.data;

                const contactUsedResponse = await axios.get('/app/contact/contactUsed');

                this.contactUsed = contactUsedResponse.data;

                const response = await axios.get('/app/myAccount/renewalEstimate');

                if (response.data === null || Object.keys(response.data).length === 0) {
                    throw new Error('Empty renewal estimate returned from KSM');
                }

                const {
                    data: {
                        subscriptionRenewalEstimate: {
                            estimate,
                            currencySymbol,
                            subscriptionPeriodUnit,
                            scheduledChangesEstimate,
                            subscriptionStatus,
                            nextBillingAt,
                            differences,
                            scheduledToCancel,
                        },
                    },
                } = response;

                this.estimate = estimate;
                this.currencySymbol = currencySymbol;
                this.periodUnit = subscriptionPeriodUnit;
                this.hasScheduledChanges = !!scheduledChangesEstimate;
                this.subscriptionStatus = subscriptionStatus;
                this.lineItems = this.estimate?.estimateLineItems?.filter(({ type }) => type !== 'PLAN');
                this.scheduledToCancel = scheduledToCancel;

                if (this.hasScheduledChanges) {
                    this.showScheduledChangePrePage = true;
                    this.scheduledChangesEstimateTotal = scheduledChangesEstimate.total;
                    this.setCurrentStage(SELECTION_STAGE);
                }

                this.nextBillingAt = nextBillingAt || '';
                this.differences = differences;

                const currentQuantity = this.lineItems?.find(({ name, type }) => {
                    return name.toLowerCase().includes('additional') && type === this.localTypeOfAddon;
                });

                if (currentQuantity !== undefined) {
                    this.currentTier = this.contactPricingTiers.findIndex((contact) => {
                        return contact.maxUnitsDecimal === currentQuantity.quantity;
                    });
                }

                this.sendAmplitudeEvent(AMPLITUDE_INITIAL_LOAD_STEP, AMPLITUDE_SUCCESS_STATUS);
            } catch (e) {
                this.errorLoadingInitialState = true;
                this.logError(e.message, e.stack);
                this.sendAmplitudeEvent(AMPLITUDE_INITIAL_LOAD_STEP, AMPLITUDE_ERROR_STATUS);
            } finally {
                this.showLoading = false;
            }
        },

        formatAddonType() {
            this.localTypeOfAddon = this.parentTypeOfAddon.toUpperCase();
        },

        closeScheduledChangeNotice() {
            this.showScheduledChangePrePage = false;
        },

        setChangeEstimate(changeEstimate) {
            this.changeEstimate = changeEstimate;
        },

        setCurrentStage(stage) {
            this.changeEstimate = {};
            this.selectedWizardStage = stage;
        },

        changeSelectedTier(selectedTier) {
            this.confirmationLoadingStatus = IDLE;
            this.selectedTier = selectedTier;
        },

        redirectForClose() {
            window.location.href = this.redirectAfterCloseUrl;
        },

        setConfirmationLoadingStatus(status) {
            this.confirmationLoadingStatus = status;
        },

        goToSupportChat() {
            window.open(this.supportUrl, '_blank');
        },

        setErrorLoadingChangeEstimate() {
            this.errorLoadingChangeEstimate = true;
        },

        logError(error, details) {
            const errorBody = {
                source: CONTACTS_QUICK_ADD,
                step: INITIAL_LOAD,
                error,
                details,
            };

            axios.post('/app/myAccount/logError', errorBody);
        },

        sendAmplitudeEvent(action, status) {
            amplitude.infusionsoft.track(amplitude.keap.events.SUBSCRIPTION_MANAGEMENT_QUICK_ADD, {
                Event: action,
                Type: this.localTypeOfAddon.toLowerCase(),
                Status: status,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.network-error {
    align-self: center;
    font-size: $font-size-sm;
    padding-bottom: $spacing-200;
}

.network-error-paragraph {
    margin: 0;
}
</style>
